import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Maximize2, 
  Scissors, 
  Download, 
  RefreshCw, 
  ArrowLeft, 
  Upload, 
  Check, 
  AlertCircle, 
  ImageIcon, 
  Settings,
  Lock,
  Unlock,
  Sparkles,
  Layers,
  Crop
} from 'lucide-react';

export const ImageResizerCropper = () => {
  const { withAd, toast } = useAdRedirect();
  const [activeTab, setActiveTab] = useState('resize'); // resize, crop
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [resultImage, setResultImage] = useState('');
  const [resultSize, setResultSize] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Resizer configurations
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(600);
  const [lockAspect, setLockAspect] = useState(true);
  const [originalAspect, setOriginalAspect] = useState(1.33);
  const [resizeFormat, setResizeFormat] = useState('image/png');

  // Cropper configurations
  const [cropX, setCropX] = useState(10);
  const [cropY, setCropY] = useState(10);
  const [cropWidth, setCropWidth] = useState(80);
  const [cropHeight, setCropHeight] = useState(80);
  const [cropAspectPreset, setCropAspectPreset] = useState('free'); // free, 1:1, 16:9, 4:3

  const fileInputRef = useRef(null);
  const imageRef = useRef(null);

  const handleReset = () => {
    setFile(null);
    setFilePreview('');
    setResultImage('');
    setResultSize('');
    setErrorMsg('');
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    setFile(selected);
    setErrorMsg('');
    setResultImage('');

    const reader = new FileReader();
    reader.onload = (event) => {
      setFilePreview(event.target.result);
      
      // Load image to get original dimensions
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        setWidth(img.naturalWidth || img.width);
        setHeight(img.naturalHeight || img.height);
        const aspect = (img.naturalWidth || img.width) / (img.naturalHeight || img.height);
        setOriginalAspect(aspect);
      };
    };
    reader.readAsDataURL(selected);
  };

  // Synchronize Resizer dimensions
  const handleWidthChange = (val) => {
    const newW = parseInt(val) || 0;
    setWidth(newW);
    if (lockAspect && originalAspect) {
      setHeight(Math.round(newW / originalAspect));
    }
  };

  const handleHeightChange = (val) => {
    const newH = parseInt(val) || 0;
    setHeight(newH);
    if (lockAspect && originalAspect) {
      setWidth(Math.round(newH * originalAspect));
    }
  };

  // Synchronize Cropper aspect presets
  useEffect(() => {
    if (cropAspectPreset === '1:1') {
      setCropHeight(cropWidth);
    } else if (cropAspectPreset === '16:9') {
      setCropHeight(Math.round(cropWidth * 9 / 16));
    } else if (cropAspectPreset === '4:3') {
      setCropHeight(Math.round(cropWidth * 3 / 4));
    }
  }, [cropWidth, cropAspectPreset]);

  // Execute Resizing
  const handleResizeAction = () => {
    if (!filePreview) return;
    setIsProcessing(true);
    setErrorMsg('');

    setTimeout(() => {
      const img = new Image();
      img.src = filePreview;
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          if (resizeFormat === 'image/jpeg') {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, width, height);
          }

          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL(resizeFormat, 0.92);
          setResultImage(dataUrl);

          const bytes = Math.round((dataUrl.length - 22) * 3 / 4);
          setResultSize(formatBytes(bytes));
          setIsProcessing(false);
        } catch (err) {
          setErrorMsg('Failed to resize image canvas nodes.');
          setIsProcessing(false);
        }
      };
    }, 1000);
  };

  // Execute Cropping
  const handleCropAction = () => {
    if (!filePreview) return;
    setIsProcessing(true);
    setErrorMsg('');

    setTimeout(() => {
      const img = new Image();
      img.src = filePreview;
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          
          // Map percentage crop box coordinates to actual image dimensions
          const actualX = (cropX / 100) * img.naturalWidth;
          const actualY = (cropY / 100) * img.naturalHeight;
          const actualW = (cropWidth / 100) * img.naturalWidth;
          const actualH = (cropHeight / 100) * img.naturalHeight;

          canvas.width = actualW;
          canvas.height = actualH;
          const ctx = canvas.getContext('2d');

          ctx.drawImage(img, actualX, actualY, actualW, actualH, 0, 0, actualW, actualH);
          const dataUrl = canvas.toDataURL('image/png');
          setResultImage(dataUrl);

          const bytes = Math.round((dataUrl.length - 22) * 3 / 4);
          setResultSize(formatBytes(bytes));
          setIsProcessing(false);
        } catch (err) {
          setErrorMsg('Failed to process cropping canvas matrices.');
          setIsProcessing(false);
        }
      };
    }, 1000);
  };

  const downloadResult = () => {
    if (!resultImage) return;
    const ext = activeTab === 'resize' ? (resizeFormat === 'image/png' ? 'png' : 'jpg') : 'png';
    const link = document.createElement('a');
    link.href = resultImage;
    link.download = `toolmetrico-${activeTab}-${Date.now()}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    return parseFloat((bytes / k).toFixed(1)) + ' KB';
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      <SEO 
        title="Free Image Resizer & Cropper - Resize Image Online" 
        description="Free online image resizer and photo cropper tool. Resize image online for Instagram, Facebook, and web instantly. 100% secure client-side processing."
        keywords="image resizer, resize image online, resize image for Instagram, crop image online, photo resizer free, image tools online"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Interactive Image Workshop</span>
      </div>



      {/* Workshop Mode selectors tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid var(--border-color)',
        marginBottom: '32px',
        gap: '24px'
      }}>
        <button
          onClick={() => { setActiveTab('resize'); setResultImage(''); }}
          style={{
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'resize' ? '3px solid var(--secondary)' : '3px solid transparent',
            color: activeTab === 'resize' ? 'var(--text-primary)' : 'var(--text-secondary)',
            fontSize: '1.05rem',
            fontWeight: 800,
            padding: '12px 6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'var(--transition-smooth)'
          }}
        >
          <Maximize2 size={16} /> Image Resizer
        </button>
        <button
          onClick={() => { setActiveTab('crop'); setResultImage(''); }}
          style={{
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'crop' ? '3px solid var(--secondary)' : '3px solid transparent',
            color: activeTab === 'crop' ? 'var(--text-primary)' : 'var(--text-secondary)',
            fontSize: '1.05rem',
            fontWeight: 800,
            padding: '12px 6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'var(--transition-smooth)'
          }}
        >
          <Scissors size={16} /> Image Cropper
        </button>
      </div>

      {/* Master Workspace Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '32px',
        alignItems: 'start'
      }} className="workshop-workspace-grid">
        
        {/* Left Column: Rules, DragDrop and configs sliders */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          
          {/* File Upload zone */}
          <div style={cardStyle}>
            <span style={labelStyle}>Select Image Asset</span>
            
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '2px dashed rgba(14, 165, 233, 0.3)',
                background: 'var(--bg-secondary)',
                borderRadius: '16px',
                padding: '40px 24px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'var(--transition-smooth)'
              }}
              className="dropzone-area"
            >
              <input 
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                style={{ display: 'none' }}
              />

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  background: 'rgba(14, 165, 233, 0.08)',
                  color: 'var(--secondary)',
                  padding: '10px',
                  borderRadius: '50%',
                  display: 'inline-flex'
                }}>
                  <Upload size={20} />
                </div>
                <div>
                  <h5 style={{ margin: '0 0 2px 0', fontSize: '0.95rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                    Choose an image to upload
                  </h5>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Supports PNG, JPG, WEBP, BMP up to 15MB.
                  </p>
                </div>
              </div>
            </div>

            {file && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'rgba(14, 165, 233, 0.03)',
                border: '1px solid var(--border-color)',
                padding: '10px 18px',
                borderRadius: '10px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <ImageIcon size={16} style={{ color: 'var(--secondary)' }} />
                  <span style={{ fontSize: '0.825rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {file.name}
                  </span>
                </div>
                <button
                  onClick={handleReset}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 700
                  }}
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* TAB 1: RESIZER CONTROLS */}
          {activeTab === 'resize' && (
            <div style={cardStyle}>
              <span style={labelStyle}>Dimension boundaries</span>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 700 }}>Width (px)</label>
                  <input 
                    type="number"
                    value={width}
                    onChange={(e) => handleWidthChange(e.target.value)}
                    style={textInputStyle}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 700 }}>Height (px)</label>
                  <input 
                    type="number"
                    value={height}
                    onChange={(e) => handleHeightChange(e.target.value)}
                    style={textInputStyle}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <button
                  onClick={() => setLockAspect(!lockAspect)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: lockAspect ? 'var(--secondary)' : 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    padding: 0
                  }}
                >
                  {lockAspect ? <Lock size={14} /> : <Unlock size={14} />}
                  Lock Aspect Ratio ({lockAspect ? 'Enabled' : 'Disabled'})
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
                <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 700 }}>Export extension format</label>
                <select
                  value={resizeFormat}
                  onChange={(e) => setResizeFormat(e.target.value)}
                  style={selectStyle}
                >
                  <option value="image/png">PNG Format</option>
                  <option value="image/jpeg">JPG Format</option>
                  <option value="image/webp">WEBP Format</option>
                </select>
              </div>
            </div>
          )}

          {/* TAB 2: CROpper SLIDER CONTROLS */}
          {activeTab === 'crop' && (
            <div style={cardStyle}>
              <span style={labelStyle}>Cropping Area Sliders</span>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                
                {/* Preset boundaries */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 700 }}>Aspect ratio crop preset</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                    {['free', '1:1', '16:9', '4:3'].map((preset) => (
                      <button
                        key={preset}
                        onClick={() => setCropAspectPreset(preset)}
                        style={{
                          height: '32px',
                          borderRadius: '6px',
                          border: cropAspectPreset === preset ? '1.5px solid var(--secondary)' : '1px solid var(--border-color)',
                          background: cropAspectPreset === preset ? 'rgba(14, 165, 233, 0.05)' : '#ffffff',
                          color: cropAspectPreset === preset ? 'var(--secondary)' : 'var(--text-secondary)',
                          fontSize: '0.72rem',
                          fontWeight: 800,
                          cursor: 'pointer'
                        }}
                      >
                        {preset.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  {/* Position X */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', fontWeight: 700 }}>
                      <span>Start X Pos</span>
                      <span>{cropX}%</span>
                    </div>
                    <input 
                      type="range"
                      min="0"
                      max={100 - cropWidth}
                      value={cropX}
                      onChange={(e) => setCropX(parseInt(e.target.value))}
                      style={{ width: '100%', accentColor: 'var(--secondary)' }}
                    />
                  </div>

                  {/* Position Y */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', fontWeight: 700 }}>
                      <span>Start Y Pos</span>
                      <span>{cropY}%</span>
                    </div>
                    <input 
                      type="range"
                      min="0"
                      max={100 - cropHeight}
                      value={cropY}
                      onChange={(e) => setCropY(parseInt(e.target.value))}
                      style={{ width: '100%', accentColor: 'var(--secondary)' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  {/* Crop Width */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', fontWeight: 700 }}>
                      <span>Crop Width</span>
                      <span>{cropWidth}%</span>
                    </div>
                    <input 
                      type="range"
                      min="10"
                      max={100 - cropX}
                      value={cropWidth}
                      onChange={(e) => setCropWidth(parseInt(e.target.value))}
                      style={{ width: '100%', accentColor: 'var(--secondary)' }}
                    />
                  </div>

                  {/* Crop Height */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', fontWeight: 700 }}>
                      <span>Crop Height</span>
                      <span>{cropHeight}%</span>
                    </div>
                    <input 
                      type="range"
                      min="10"
                      max={100 - cropY}
                      disabled={cropAspectPreset !== 'free'}
                      value={cropHeight}
                      onChange={(e) => setCropHeight(parseInt(e.target.value))}
                      style={{ width: '100%', accentColor: 'var(--secondary)', cursor: cropAspectPreset !== 'free' ? 'not-allowed' : 'pointer' }}
                    />
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* Action Trigger button */}
          <button
            onClick={activeTab === 'resize' ? handleResizeAction : handleCropAction}
            disabled={isProcessing || !filePreview}
            style={{
              height: '48px',
              border: 'none',
              background: 'linear-gradient(90deg, var(--secondary) 0%, var(--primary) 100%)',
              color: '#ffffff',
              borderRadius: '12px',
              fontSize: '0.95rem',
              fontWeight: 700,
              cursor: isProcessing || !filePreview ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'var(--transition-smooth)'
            }}
            className="workshop-action-btn"
          >
            {isProcessing ? <RefreshCw size={16} className="spinner" /> : <Settings size={16} />}
            {isProcessing ? 'Processing canvas images...' : activeTab === 'resize' ? 'Resize Image Boundaries' : 'Crop Selection Box'}
          </button>

          {errorMsg && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.15)',
              padding: '14px 20px',
              borderRadius: '12px',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '0.85rem',
              fontWeight: 600
            }}>
              <AlertCircle size={16} /> {errorMsg}
            </div>
          )}

        </div>

        {/* Right Column: Previews display overlays */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }} className="workshop-preview-column">
          
          {/* Active Canvas Input preview containing Crop box indicator overlay */}
          {filePreview ? (
            <div style={cardStyle}>
              <span style={labelStyle}>Live Editor Workspace</span>
              
              <div style={{
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid var(--border-color)',
                aspectRatio: '16/11',
                background: 'var(--bg-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                <img 
                  ref={imageRef}
                  src={filePreview} 
                  alt="Editor Workspace" 
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />

                {/* Virtual transparent crop highlight bounding box overlay (only in Crop tab mode) */}
                {activeTab === 'crop' && (
                  <div style={{
                    position: 'absolute',
                    top: `${cropY}%`,
                    left: `${cropX}%`,
                    width: `${cropWidth}%`,
                    height: `${cropHeight}%`,
                    border: '2px solid var(--secondary)',
                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {/* Bounding handles corners */}
                    <div style={{ position: 'absolute', top: '-4px', left: '-4px', width: '8px', height: '8px', background: 'var(--secondary)' }} />
                    <div style={{ position: 'absolute', top: '-4px', right: '-4px', width: '8px', height: '8px', background: 'var(--secondary)' }} />
                    <div style={{ position: 'absolute', bottom: '-4px', left: '-4px', width: '8px', height: '8px', background: 'var(--secondary)' }} />
                    <div style={{ position: 'absolute', bottom: '-4px', right: '-4px', width: '8px', height: '8px', background: 'var(--secondary)' }} />
                    <Crop size={18} style={{ color: '#ffffff', opacity: 0.8 }} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ ...cardStyle, padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <ImageIcon size={32} style={{ margin: '0 auto 8px auto', opacity: 0.6 }} />
              <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>Upload an image to start editing</span>
            </div>
          )}

          {/* Result outputs downloaded file card */}
          {resultImage && (
            <div style={{
              background: '#ffffff',
              border: '1px solid var(--border-color)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: 'var(--shadow-md)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ ...labelStyle, color: '#059669' }}>Asset Rendered Success</span>
                <span style={{ fontSize: '0.65rem', background: 'rgba(5, 150, 105, 0.08)', color: '#059669', padding: '2px 8px', borderRadius: '4px', fontWeight: 800 }}>
                  Ready
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid var(--border-color)',
                  aspectRatio: '16/11',
                  background: 'var(--bg-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img 
                    src={resultImage} 
                    alt="Success Result" 
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  <span>Calculated Size: <strong style={{ color: '#059669' }}>{resultSize}</strong></span>
                  <span>Dimensions: <strong>{activeTab === 'resize' ? `${width} x ${height} px` : 'Custom Crop'}</strong></span>
                </div>

                <AdToast message={toast} />
                <button
                  onClick={withAd('img-resize', downloadResult)}
                  style={{
                    height: '42px',
                    border: 'none',
                    background: '#059669',
                    color: '#ffffff',
                    borderRadius: '10px',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'var(--transition-smooth)'
                  }}
                  className="download-workshop-btn"
                >
                  <Download size={14} /> Download Final Asset
                </button>
              </div>
            </div>
          )}

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .workshop-workspace-grid {
            grid-template-columns: 1fr 440px !important;
          }
        }

        .dropzone-area:hover {
          border-color: var(--secondary) !important;
          background: rgba(14, 165, 233, 0.01) !important;
        }

        .workshop-action-btn:hover {
          opacity: 0.95;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(14, 165, 233, 0.15) !important;
        }

        .download-workshop-btn:hover {
          background: #047857 !important;
          transform: translateY(-1px);
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

    {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="image-workshop" />
    </div>
  );
};

// Styling Rules
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  boxShadow: 'var(--shadow-sm)'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const textInputStyle = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1px solid var(--border-color)',
  fontSize: '0.9rem',
  fontWeight: 600,
  outline: 'none',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box'
};

const selectStyle = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1px solid var(--border-color)',
  fontSize: '0.9rem',
  fontWeight: 600,
  outline: 'none',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box',
  cursor: 'pointer'
};

export default ImageResizerCropper;
